/**
 * Requirement store using Zustand.
 */

import { create } from 'zustand';

import {
  listRequirements,
  getRequirementOverview,
  listDesignDocuments,
} from '@/api/requirements';
import { SUBCATEGORY_OPTIONS } from '@/constants/requirements';
import type {
  Requirement,
  RequirementOverviewResponse,
  SubcategoryType,
  DesignDocument,
} from '@/types';

interface RequirementState {
  requirements: Requirement[];
  allRequirements: Requirement[];
  /** Which project `allRequirements` actually belongs to. Null until a fetch succeeds,
   *  so consumers never treat stale/other-project data as the current project's truth. */
  allRequirementsProjectId: number | null;
  overview: RequirementOverviewResponse | null;
  currentSubcategory: SubcategoryType | null;
  isLoading: boolean;
  error: string | null;
  designDocuments: DesignDocument[];
  designDocumentsLoading: boolean;

  // Actions
  fetchRequirements: (projectId: number, subcategory?: string) => Promise<void>;
  fetchAllRequirements: (projectId: number) => Promise<void>;
  fetchOverview: (projectId: number) => Promise<void>;
  fetchDesignDocuments: (projectId: number) => Promise<void>;
  setCurrentSubcategory: (subcategory: SubcategoryType | null) => void;
  clearError: () => void;
  reset: () => void;
}

export const useRequirementStore = create<RequirementState>()((set) => ({
  requirements: [],
  allRequirements: [],
  allRequirementsProjectId: null,
  overview: null,
  currentSubcategory: null,
  isLoading: false,
  error: null,
  designDocuments: [],
  designDocumentsLoading: false,

  fetchRequirements: async (projectId: number, subcategory?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await listRequirements(projectId, subcategory);
      set({ requirements: response.requirements, isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取需求列表失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchAllRequirements: async (projectId: number) => {
    try {
      const results = await Promise.all(
        SUBCATEGORY_OPTIONS.map((opt) => listRequirements(projectId, opt.key))
      );
      const flatAll = results.flatMap((r) => r.requirements);

      // Deduplicate by reqId to prevent React duplicate key errors
      const seenIds = new Set<string>();
      const all = flatAll.filter((req) => {
        if (seenIds.has(req.reqId)) return false;
        seenIds.add(req.reqId);
        return true;
      });

      set({ allRequirements: all, allRequirementsProjectId: projectId });
    } catch {
      // Silently fail — allRequirements is supplementary data for filtering
    }
  },

  fetchOverview: async (projectId: number) => {
    set({ isLoading: true, error: null });
    try {
      const overview = await getRequirementOverview(projectId);
      set({ overview, isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取需求总览失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchDesignDocuments: async (projectId: number) => {
    set({ designDocumentsLoading: true, error: null });
    try {
      const response = await listDesignDocuments(projectId);
      set({ designDocuments: response.documents, designDocumentsLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取设计文档失败';
      set({ error: message, designDocumentsLoading: false });
    }
  },

  setCurrentSubcategory: (subcategory: SubcategoryType | null) => {
    set({ currentSubcategory: subcategory });
  },

  clearError: () => set({ error: null }),

  reset: () => set({
    requirements: [],
    allRequirements: [],
    allRequirementsProjectId: null,
    overview: null,
    currentSubcategory: null,
    isLoading: false,
    error: null,
    designDocuments: [],
    designDocumentsLoading: false,
  }),
}));
